import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import { MAPBOX_TOKEN } from '../../../utils/constants';
import { createCircle } from '../../map/utils/geometryUtils';
import './ProcessorMapView.css';

if (typeof mapboxgl !== 'undefined' && MAPBOX_TOKEN && !mapboxgl.accessToken) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

const RADIUS_SOURCE_ID = 'processor-radius';
const RADIUS_FILL_LAYER_ID = 'processor-radius-fill';
const RADIUS_LINE_LAYER_ID = 'processor-radius-line';
const RING_COLOR = '#B4501C';

function milesToYards(miles) {
  return Math.max(0, Number(miles) || 0) * 1760;
}

export function processorMapZoom(radiusMiles) {
  const radius = Math.max(Number(radiusMiles) || 10, 0.0001);
  return Math.max(6, Math.min(11, 9.4 - Math.log2(radius / 10)));
}

export function processorMapSignature(center, radiusMiles, rows = []) {
  const lat = center?.latitude;
  const lng = center?.longitude;
  const rowSig = rows.map((row) => `${row.id}${row.freshness || ''}`).join(',');
  return `${lat}|${lng}|${radiusMiles}|${rowSig}`;
}

function placeKey(center, radiusMiles) {
  return `${center?.latitude}|${center?.longitude}|${radiusMiles}`;
}

function ensureRadiusLayers(map) {
  if (!map.isStyleLoaded()) return false;
  if (map.getSource(RADIUS_SOURCE_ID)) return true;
  map.addSource(RADIUS_SOURCE_ID, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: RADIUS_FILL_LAYER_ID,
    type: 'fill',
    source: RADIUS_SOURCE_ID,
    paint: {
      'fill-color': RING_COLOR,
      'fill-opacity': 0.07,
    },
  });
  map.addLayer({
    id: RADIUS_LINE_LAYER_ID,
    type: 'line',
    source: RADIUS_SOURCE_ID,
    paint: {
      'line-color': RING_COLOR,
      'line-width': 2,
      'line-dasharray': [2, 2],
    },
  });
  return true;
}

export function ProcessorMapView({
  center,
  radiusMiles,
  rows = [],
  activeId = null,
  onSelect,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const centerMarkerRef = useRef(null);
  const signatureRef = useRef('');
  const placeKeyRef = useRef('');

  const latitude = center.latitude;
  const longitude = center.longitude;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    if (typeof window === 'undefined' || !MAPBOX_TOKEN || !mapboxgl?.Map) return undefined;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: [longitude, latitude],
      zoom: processorMapZoom(radiusMiles),
      attributionControl: false,
    });
    mapRef.current = map;
    if (mapboxgl.AttributionControl) {
      map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
    }
    map.on('load', () => {
      ensureRadiusLayers(map);
      map.resize();
    });

    return () => {
      markersRef.current.forEach(({ marker }) => marker.remove());
      markersRef.current = [];
      centerMarkerRef.current?.remove();
      centerMarkerRef.current = null;
      map.remove();
      mapRef.current = null;
      signatureRef.current = '';
      placeKeyRef.current = '';
    };
    // Map instance is created once; camera/data updates live in the draw effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    const mapCenter = { latitude, longitude };
    const draw = () => {
      if (!map.isStyleLoaded()) return;
      if (!ensureRadiusLayers(map)) return;

      const signature = processorMapSignature(mapCenter, radiusMiles, rows);
      const nextPlaceKey = placeKey(mapCenter, radiusMiles);
      const moved = placeKeyRef.current !== nextPlaceKey;
      const sameDraw = signatureRef.current === signature;

      if (!sameDraw) {
        signatureRef.current = signature;
        const circle = createCircle(longitude, latitude, milesToYards(radiusMiles));
        const radiusSource = map.getSource(RADIUS_SOURCE_ID);
        if (radiusSource && circle) {
          radiusSource.setData({
            type: 'Feature',
            geometry: circle,
            properties: {},
          });
        }

        markersRef.current.forEach(({ marker }) => marker.remove());
        markersRef.current = [];
        centerMarkerRef.current?.remove();

        const centerEl = document.createElement('div');
        centerEl.className = 'processor-map__center';
        centerEl.setAttribute('data-testid', 'processor-map-center');
        centerEl.setAttribute('aria-label', 'Search centre');
        centerMarkerRef.current = new mapboxgl.Marker({ element: centerEl })
          .setLngLat([longitude, latitude])
          .addTo(map);

        rows.forEach((row) => {
          if (!Number.isFinite(row.latitude) || !Number.isFinite(row.longitude)) return;
          const yona = row.source === 'yona';
          const el = document.createElement('button');
          el.type = 'button';
          el.className = `processor-map__pin ${yona ? 'processor-map__pin--yona' : 'processor-map__pin--place'}`;
          el.textContent = yona ? '✦' : '·';
          el.setAttribute('aria-label', row.name);
          el.title = row.name;
          el.addEventListener('click', (event) => {
            event.stopPropagation();
            onSelect?.(row.id);
          });
          const marker = new mapboxgl.Marker({ element: el })
            .setLngLat([row.longitude, row.latitude])
            .addTo(map);
          markersRef.current.push({ marker, id: row.id, el });
        });
      }

      markersRef.current.forEach(({ el, id }) => {
        el.classList.toggle('is-on', String(id) === String(activeId));
      });

      if (moved) {
        placeKeyRef.current = nextPlaceKey;
        map.easeTo({
          center: [longitude, latitude],
          zoom: processorMapZoom(radiusMiles),
          duration: 420,
        });
      }
    };

    if (map.isStyleLoaded()) {
      draw();
      return undefined;
    }
    map.once('load', draw);
    return undefined;
  }, [activeId, latitude, longitude, onSelect, radiusMiles, rows]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="processor-map processor-map--unavailable" data-testid="processor-map-unavailable">
        Map preview is unavailable right now.
      </div>
    );
  }

  return (
    <div className="processor-map" data-testid="processor-map">
      <div className="processor-map__canvas" ref={containerRef} />
    </div>
  );
}

ProcessorMapView.propTypes = {
  center: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }).isRequired,
  radiusMiles: PropTypes.number.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func,
};
